class CartImporter extends HTMLElement {

  constructor() {
    super();
    if (!this.dataset.storefrontAccessToken) {
      throw new Error(this.I18N.errors.missing_storefront_token);
    }
    this.SUPPORTED_FILE_EXTENSIONS = [".csv", ".xlsx"];
    this.STOREFRONT_API_VERSION = "2026-01";
    this.STOREFRONT_BULK_SKU_CHUNK_SIZE = 40;
    this.DOM_SELECTORS = {
      openButton: "[data-cart-importer-open]",
      modal: "#cart-importer-modal",
      trigger: "[data-cart-importer-trigger]",
      fileInput: "[data-cart-importer-input]",
      status: "[data-cart-importer-status]",
      message: "[data-cart-importer-message]",
      footerDefault: "[data-cart-importer-footer-default]",
      footerProceed: "[data-cart-importer-footer-proceed]",
      proceed: "[data-cart-importer-proceed]"
    };
    this.isCompleted = false;
    this.I18N = JSON.parse(this.querySelector("[data-cart-importer-i18n]").textContent.replaceAll("&quot;", '"').replaceAll("=&gt;", ":"));
    this.dom = {};
  }

  connectedCallback() {
    this.dom = Object.fromEntries(Object.entries(this.DOM_SELECTORS).map(([key, selector]) => [key, this.querySelector(selector)]));
    document.body.appendChild(this.dom.modal);
    this.dom.openButton.addEventListener("click", this.handleOpenClick);
    this.dom.trigger.addEventListener("click", this.handleTriggerClick);
    this.dom.fileInput.addEventListener("change", this.handleFileChange);
    this.dom.proceed.addEventListener("click", this.handleProceedClick);
    this.dom.modal.addEventListener("click", this.handleModalBackdropClick);
    this.dom.modal.addEventListener("keydown", this.handleModalKeydown);
    this.dom.modal.addEventListener("dialog:after-show", this.handleModalOpen);
  }

  disconnectedCallback() {
    if (this.isCompleted && this.dom.modal) {
      this.dom.proceed.addEventListener("click", this.handleProceedClick, { once: true });
      this.dom.openButton?.removeEventListener("click", this.handleOpenClick);
      this.dom.trigger?.removeEventListener("click", this.handleTriggerClick);
      this.dom.fileInput?.removeEventListener("change", this.handleFileChange);
      this.dom.modal.removeEventListener("click", this.handleModalBackdropClick);
      this.dom.modal.removeEventListener("keydown", this.handleModalKeydown);
      this.dom.modal.removeEventListener("dialog:after-show", this.handleModalOpen);
      return;
    }
    this.dom.modal?.remove();
    this.dom.openButton?.removeEventListener("click", this.handleOpenClick);
    this.dom.trigger?.removeEventListener("click", this.handleTriggerClick);
    this.dom.fileInput?.removeEventListener("change", this.handleFileChange);
    this.dom.proceed?.removeEventListener("click", this.handleProceedClick);
    this.dom.modal?.removeEventListener("click", this.handleModalBackdropClick);
    this.dom.modal?.removeEventListener("keydown", this.handleModalKeydown);
    this.dom.modal?.removeEventListener("dialog:after-show", this.handleModalOpen);
  }

  handleOpenClick = () => {
    this.dom.modal.show();
  };

  handleTriggerClick = () => {
    this.dom.fileInput.click();
  };

  handleProceedClick = () => {
    const cartPath = `${window.Shopify.routes.root}cart`;
    if (window.location.pathname === cartPath) {
      window.location.reload();
    } else {
      window.location.href = cartPath;
    }
  };

  handleModalBackdropClick = (event) => {
    if (this.isCompleted) return;
    if (event.target === this.dom.modal) this.dom.modal.hide();
  };

  handleModalKeydown = (event) => {
    if (this.isCompleted && event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  handleModalOpen = () => {
    this.isCompleted = false;
    this.dom.footerDefault.hidden = false;
    this.dom.footerProceed.hidden = true;
    this.showStatus("info", "", false);
  };

  handleFileChange = async (event) => {
    const selectedFile = event.target.files[0];

    try {
      this.setLoadingState(true);

      const parsedRows = await this.parseFile(selectedFile);
      if (parsedRows.length === 0) {
        throw new Error(this.I18N.errors.no_valid_rows);
      }

      const skuTotals = this.groupRowsBySku(parsedRows);
      const resolution = await this.resolveSkus(Array.from(skuTotals.keys()));

      const unavailableSkus = [];
      const notFoundSkus = [...resolution.notFoundSkus];
      const itemsToAdd = [];

      for (const [sku, quantity] of skuTotals.entries()) {
        const variant = resolution.skuToVariant.get(sku);
        if (!variant) {
          notFoundSkus.push(sku);
          continue;
        }

        if (!variant.available) {
          unavailableSkus.push(sku);
          continue;
        }

        itemsToAdd.push({ id: variant.id, quantity });
      }

      if (itemsToAdd.length > 0) {
        await this.addItemsToCart(itemsToAdd);
      }

      this.renderResult({
        addedCount: itemsToAdd.length,
        notFoundSkus: [...new Set(notFoundSkus)],
        unavailableSkus: [...new Set(unavailableSkus)]
      });

      if (itemsToAdd.length > 0) {
        this.isCompleted = true;
        this.dom.footerDefault.hidden = true;
        this.dom.footerProceed.hidden = false;
      }
    } catch (error) {
      this.showStatus("error", error.message);
    } finally {
      this.setLoadingState(false);
      this.dom.fileInput.value = "";
    }
  };

  async parseFile(file) {
    const name = file.name.toLowerCase();
    const extension = this.SUPPORTED_FILE_EXTENSIONS.find((ext) => name.endsWith(ext));
    if (!extension) throw new Error(this.I18N.errors.invalid_file_format);
    if (extension === ".csv") return this.parseCsvRows(await file.text());
    return this.parseXlsxRows(file);
  }

  parseCsvRows(fileContent) {
    const lines = fileContent.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n").filter((line) => line.trim() !== "");
    if (lines.length < 2) {
      throw new Error(this.I18N.errors.no_data_rows);
    }

    const delimiter = this.detectCsvDelimiter(lines[0]);
    const rows = lines.map((line) => this.parseCsvLine(line, delimiter));
    return this.parseTabularRows(rows);
  }

  detectCsvDelimiter(headerLine) {
    const commaCount = (headerLine.match(/,/g) || []).length;
    const semicolonCount = (headerLine.match(/;/g) || []).length;
    return semicolonCount > commaCount ? ";" : ",";
  }

  parseCsvLine(line, delimiter) {
    const values = [];
    let current = "";
    let inQuotes = false;

    for (let index = 0; index < line.length; index += 1) {
      const character = line[index];
      const nextCharacter = line[index + 1];

      if (character === '"' && inQuotes && nextCharacter === '"') {
        current += '"';
        index += 1;
        continue;
      }

      if (character === '"') {
        inQuotes = !inQuotes;
        continue;
      }

      if (character === delimiter && !inQuotes) {
        values.push(current.trim());
        current = "";
        continue;
      }

      current += character;
    }

    values.push(current.trim());
    return values;
  }

  async parseXlsxRows(file) {
    const arrayBuffer = await file.arrayBuffer();
    const xlsx = await import("https://cdn.jsdelivr.net/npm/xlsx@0.18.5/+esm");
    const workbook = xlsx.read(arrayBuffer, { type: "array" });
    const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
    const rows = xlsx.utils.sheet_to_json(firstSheet, { header: 1, blankrows: false, raw: false });
    return this.parseTabularRows(rows);
  }

  parseTabularRows(rows) {
    if (rows.length < 2) {
      return [];
    }

    const parsedRows = [];

    for (let rowIndex = 1; rowIndex < rows.length; rowIndex += 1) {
      const currentRow = rows[rowIndex];
      const sku = String(currentRow[0] ?? "").trim().toUpperCase();
      const quantity = parseInt(String(currentRow[1] ?? ""), 10);

      if (!sku || !Number.isInteger(quantity) || quantity <= 0) continue;
      parsedRows.push({ sku, quantity });
    }

    return parsedRows;
  }

  groupRowsBySku(rows) {
    return rows.reduce((totals, row) => {
      totals.set(row.sku, (totals.get(row.sku) ?? 0) + row.quantity);
      return totals;
    }, new Map());
  }

  async resolveSkus(skus) {
    const skuToVariant = await this.resolveSkusViaStorefrontApiBulk(skus);
    const notFoundSkus = skus.filter((sku) => !skuToVariant.has(sku));

    return { skuToVariant, notFoundSkus };
  }

  async resolveSkusViaStorefrontApiBulk(skus) {
    const results = new Map();

    for (let i = 0; i < skus.length; i += this.STOREFRONT_BULK_SKU_CHUNK_SIZE) {
      const chunkResults = await this.resolveSkuChunkViaStorefrontApi(skus.slice(i, i + this.STOREFRONT_BULK_SKU_CHUNK_SIZE));
      chunkResults.forEach((variant, sku) => results.set(sku, variant));
    }

    return results;
  }

  async resolveSkuChunkViaStorefrontApi(skus) {
    const results = new Map();
    const query = `
      query ProductsBySkuBulk($query: String!) {
        products(first: 250, query: $query) {
          nodes {
            variants(first: 250) {
              nodes {
                id
                sku
                barcode
                availableForSale
              }
            }
          }
        }
      }
    `;

    const response = await fetch(`${window.Shopify.routes.root}api/${this.STOREFRONT_API_VERSION}/graphql.json`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": this.dataset.storefrontAccessToken
      },
      body: JSON.stringify({
        query,
        variables: {
          query: this.buildStorefrontSkuQuery(skus)
        }
      })
    });

    if (!response.ok) return results;

    const payload = await response.json();
    if (payload.errors?.length) return results;

    const products = payload.data.products.nodes;
    const targetIds = new Set(skus);

    for (const product of products) {
      for (const node of product.variants.nodes) {
        const variantData = { id: parseInt(node.id.split("/").pop(), 10), available: node.availableForSale };
        const normalizedSku = (node.sku || "").trim().toUpperCase();
        const normalizedBarcode = (node.barcode || "").trim().toUpperCase();

        if (normalizedSku && targetIds.has(normalizedSku)) results.set(normalizedSku, variantData);
        if (normalizedBarcode && targetIds.has(normalizedBarcode)) results.set(normalizedBarcode, variantData);
      }
    }

    return results;
  }

  buildStorefrontSkuQuery(skus) {
    const terms = skus.map((sku) => {
      const escaped = sku.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
      return `(sku:"${escaped}" OR variants.sku:"${escaped}" OR barcode:"${escaped}" OR variants.barcode:"${escaped}")`;
    });

    return `(${terms.join(" OR ")})`;
  }

  async addItemsToCart(items) {
    const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items })
    });

    if (!response.ok) {
      throw new Error(this.I18N.errors.add_to_cart_failed);
    }

    const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`);
    if (!cartResponse.ok) return;

    const cart = await cartResponse.json();
    document.documentElement.dispatchEvent(new CustomEvent("cart:change", { bubbles: true, detail: { cart, baseEvent: "cart-importer:add" } }));
    document.dispatchEvent(new CustomEvent("cart:refresh"));
  }

  setLoadingState(isLoading) {
    this.dom.trigger.toggleAttribute("aria-busy", isLoading);
    this.dom.trigger.disabled = isLoading;
    if (isLoading) this.showStatus("loading", this.I18N.processing);
  }

  renderResult({ addedCount, notFoundSkus, unavailableSkus }) {
    const messages = [];
    if (addedCount > 0) {
      messages.push(this.I18N.results.added_rows.replace("{{ count }}", String(addedCount)));
    } else {
      messages.push(this.I18N.results.no_rows_added);
    }

    if (notFoundSkus.length > 0) {
      messages.push(`${this.I18N.results.not_found_prefix}: ${notFoundSkus.join(", ")}`);
    }

    if (unavailableSkus.length > 0) {
      messages.push(`${this.I18N.results.unavailable_prefix}: ${unavailableSkus.join(", ")}`);
    }

    this.showStatus("info", messages.join("\n"));
  }

  showStatus(type, message, visible = true) {
    this.dom.status.hidden = !visible;
    this.dom.message.textContent = message;
    this.dom.status.classList.remove("is-loading", "is-error", "is-info");
    if (visible) {
      this.dom.status.classList.add(type === "error" ? "is-error" : type === "loading" ? "is-loading" : "is-info");
    }
  }
}

if (!window.customElements.get("cart-importer")) {
  window.customElements.define("cart-importer", CartImporter);
}


const patchVariantPicker = async () => {
  await window.customElements.whenDefined("variant-picker");
  const preferredVariantKeyword = "handtuch";
  let lastBaseColor = null;
  let lastColorName = null;
  let pendingRaf = null;
  let hasInstalledObserver = false;

  const getVariantColorName = (image) => {
    const variantTitle = image.getAttribute("data-variant-title")?.toLowerCase()?.trim();
    return variantTitle?.split(" / ")[0]?.trim();
  };

  const applyBaseColorToHorizontalProducts = (baseColor, colorName) => {
    for (const horizontalProduct of document.querySelectorAll(".horizontal-product")) {
      const images = Array.from(horizontalProduct.querySelectorAll(".horizontal-product__image"));
      for (const image of images) {
        image.hidden = true;
      }
      const drawer = horizontalProduct.querySelector("quick-buy-drawer");
      // Prefer variants with the identical color name, fall back to variants with the same base color
      const sameNameHorizontalImages = colorName ? images.filter(image => image.hasAttribute("data-basecolor") && getVariantColorName(image) === colorName) : [];
      const sameColorHorizontalImages = sameNameHorizontalImages.length > 0
        ? sameNameHorizontalImages
        : images.filter(image => image.getAttribute("data-basecolor")?.trim() === baseColor);
      const preferredSameColorHorizontalImage = sameColorHorizontalImages.find(image => {
        const variantTitle = image.getAttribute("data-variant-title")?.toLowerCase()?.trim();
        return variantTitle?.includes(preferredVariantKeyword);
      });
      const sameColorHorizontalImage = preferredSameColorHorizontalImage || sameColorHorizontalImages[0];
      if (sameColorHorizontalImage) {
        sameColorHorizontalImage.hidden = false;
        const handle = sameColorHorizontalImage.getAttribute("data-handle");
        if (handle && drawer) {
          drawer.setAttribute("handle", handle);
          drawer._hasLoaded = false;
        }
        continue;
      }
      const fallbackHorizontalImage = images.find(image => !image.hasAttribute("data-basecolor"));
      if (fallbackHorizontalImage) {
        fallbackHorizontalImage.hidden = false;
        const handle = fallbackHorizontalImage.getAttribute("data-handle");
        if (handle && drawer) {
          drawer.setAttribute("handle", handle);
          drawer._hasLoaded = false;
        }
      }
    }
  };

  const applyBaseColorToProductCards = (baseColor, colorName) => {
    for (const optionList of document.querySelectorAll(".shopify-section--product-recommendations .product-card__swatch-list, .shopify-section--product-recommendations .product-card__variant-list, .shopify-section--recently-viewed-products .product-card__swatch-list, .shopify-section--recently-viewed-products .product-card__variant-list")) {
      // Prefer the swatch with the identical color name, fall back to the first swatch with the same base color
      const inputs = Array.from(optionList.querySelectorAll("input[data-basecolor]"));
      const input = (colorName && inputs.find(element => element.getAttribute("data-option-name") === colorName))
        || inputs.find(element => element.getAttribute("data-basecolor") === baseColor);
      if (!input) {
        continue;
      }
      const label = input.nextElementSibling;
      if (label && label.classList.contains("hidden")) {
        const siblings = optionList.querySelectorAll("label");
        if (siblings.length > 4) {
          const visibleSiblings = Array.from(siblings).filter(sibling => !sibling.classList.contains("hidden"));
          visibleSiblings[visibleSiblings.length - 1]?.classList.add("hidden");
        }
        label.classList.remove("hidden");
      }
      if (!input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }
  };

  const scheduleApply = () => {
    if (!lastBaseColor) {
      return;
    }
    if (pendingRaf) {
      cancelAnimationFrame(pendingRaf);
    }
    pendingRaf = requestAnimationFrame(() => {
      pendingRaf = null;
      lastBaseColor && applyBaseColorToHorizontalProducts(lastBaseColor, lastColorName);
      lastBaseColor && applyBaseColorToProductCards(lastBaseColor, lastColorName);
    });
  };

  const VariantPickerClass = window.customElements.get("variant-picker");
  if (!VariantPickerClass || VariantPickerClass.__patchedByCustomJs) {
    return;
  }

  const originalSelectCombination = VariantPickerClass.prototype.selectCombination;

  VariantPickerClass.prototype.selectCombination = async function patchedSelectCombination(...args) {
    const result = await originalSelectCombination.apply(this, args);

    const selectorElement = this.querySelector(`.variant-picker__option-values > input[data-basecolor][data-variant-id="${this.selectedVariant?.id}"]`);
    const baseColor = selectorElement?.getAttribute("data-basecolor")?.trim();
    const colorName = selectorElement?.getAttribute("data-option-name")?.trim() || null;
    if (baseColor) {
      lastBaseColor = baseColor;
      lastColorName = colorName;
      baseColor && applyBaseColorToHorizontalProducts(baseColor, colorName);
      baseColor && applyBaseColorToProductCards(baseColor, colorName);
      scheduleApply();
    }
    if (!hasInstalledObserver) {
      hasInstalledObserver = true;
      const observer = new MutationObserver(() => scheduleApply());
      observer.observe(document.documentElement, { childList: true, subtree: true });
    }
    return result;
  };

  VariantPickerClass.__patchedByCustomJs = true;

  const mainVariantPicker = document.querySelector(".shopify-section--main-product variant-picker[form-id]");
  const variantInput = mainVariantPicker?.querySelector(`input[data-variant-id="${mainVariantPicker?.selectedVariant?.id}"]`);
  variantInput?.dispatchEvent(new Event("change", { bubbles: true }));
};

patchVariantPicker();
